import React, {useEffect, useState} from 'react';
import Modal from 'react-modal';
import {showScene, hideScene, uploadSceneImage} from '../../sceneRepository';
import {isSceneLive} from '../../scene';
import {vtmT} from '../../i18n';
import '../../../styles/vtm/roll.css';

/**
 * Le MJ montre une image à tout ou partie de la coterie.
 * Sans nouveau fichier, la dernière image envoyée est réutilisée.
 */
const VtmSceneModal = ({isOpen, onClose, campaignUid, characters, scene}) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [selected, setSelected] = useState([]);
  const [caption, setCaption] = useState('');
  const [state, setState] = useState(null); // null | 'uploading' | 'tooBig' | 'error'
  const live = isSceneLive(scene);
  const imageUrl = preview || scene?.url || '';

  // À l'ouverture : toute la coterie par défaut, et pas de fichier en attente
  useEffect(() => {
    if (!isOpen) return;
    setFile(null);
    setPreview('');
    setState(null);
    setSelected(characters.map(character => character.uid));
    setCaption(scene?.caption || '');
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);

  const chooseFile = (event) => {
    const chosen = event.target.files[0];
    if (!chosen) return;
    setFile(chosen);
    setPreview(URL.createObjectURL(chosen));
    setState(null);
  };

  const toggle = (uid) => setSelected(selected.includes(uid) ? selected.filter(id => id !== uid) : [...selected, uid]);
  const allSelected = characters.length > 0 && selected.length === characters.length;

  const show = async () => {
    setState('uploading');
    try {
      const url = file ? await uploadSceneImage(campaignUid, file) : scene.url;
      await showScene(campaignUid, url, selected, caption);
      onClose();
    } catch (e) {
      console.log(e);
      setState(e.message === 'tooBig' ? 'tooBig' : 'error');
    }
  };

  const stop = async () => {
    try {
      await hideScene(campaignUid);
      onClose();
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} className="vtmModal" overlayClassName="vtmModalOverlay" contentLabel={vtmT('sceneShow')}>
      <div className="vtmModalHeader">
        <h2 className="vtmModalTitle">{vtmT('sceneShow')}</h2>
      </div>
      <p className="vtmModalHint">{vtmT('sceneHint')}</p>

      <div className="vtmScenePick">
        {imageUrl
          ? <img className="vtmScenePreview" src={imageUrl} alt="" />
          : <div className="vtmScenePreview empty">{vtmT('sceneNoImage')}</div>}
        <div>
          <input id="vtmSceneInput" className="vtmVisuallyHidden" type="file" accept="image/*" onChange={chooseFile} />
          <label className="vtmButton vtmFileButton" htmlFor="vtmSceneInput">{imageUrl ? vtmT('sceneChange') : vtmT('sceneChoose')}</label>
        </div>
      </div>

      <div className="vtmField">
        <label className="vtmFieldLabel" htmlFor="vtmSceneCaption">{vtmT('sceneCaption')}</label>
        <input id="vtmSceneCaption" type="text" maxLength={80} value={caption} placeholder={vtmT('sceneCaptionPlaceholder')} onChange={event => setCaption(event.target.value)} />
      </div>

      <fieldset className="vtmSceneRecipients">
        <legend className="vtmFieldLabel">{vtmT('sceneRecipients')}</legend>
        {characters.length === 0 ? (
          <p className="vtmMutedInline">{vtmT('noCharacters')}</p>
        ) : (
          <div className="vtmToggles">
            <label className="vtmToggle" htmlFor="vtmSceneAll">
              <input id="vtmSceneAll" type="checkbox" checked={allSelected} onChange={() => setSelected(allSelected ? [] : characters.map(character => character.uid))} />
              <span>{vtmT('sceneEveryone')}</span>
            </label>
            {characters.map(character => (
              <label key={character.uid} className="vtmToggle vtmSceneMember" htmlFor={`vtmScene${character.uid}`}>
                <input id={`vtmScene${character.uid}`} type="checkbox" checked={selected.includes(character.uid)} onChange={() => toggle(character.uid)} />
                <span>{character.name || vtmT('newCharacter')}</span>
              </label>
            ))}
          </div>
        )}
      </fieldset>

      {state === 'uploading' && <p className="vtmMutedInline" role="status">{vtmT('uploading')}</p>}
      {state === 'tooBig' && <p className="vtmXpError" role="alert">{vtmT('sceneTooBig')}</p>}
      {state === 'error' && <p className="vtmXpError" role="alert">{vtmT('sceneError')}</p>}

      <div className="vtmModalActions">
        <button type="button" className="vtmButton primary" onClick={show} disabled={!imageUrl || selected.length === 0 || state === 'uploading'}>
          {vtmT('sceneSend')}
        </button>
        {live && <button type="button" className="vtmButton danger" onClick={stop}>{vtmT('sceneStop')}</button>}
        <button type="button" className="vtmButton ghost" onClick={onClose}>{vtmT('close')}</button>
      </div>
    </Modal>
  );
};

export default VtmSceneModal;
